import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { Tooltip } from "@vd/ui";
import { Copy, ExternalLink } from "lucide-react";
import { useMemo, useState } from "react";
import { errorMessage } from "../../api/client";
import { connectedQuery, ops, qk, releasesQuery, whatsNewQuery } from "../../api/ops";
import type { AdminRelease, ConnectedApps, ReleaseChannel, ReleaseList } from "../../api/types";
import { DataTable } from "../../components/DataTable";
import { ReleaseNotes } from "../../components/ReleaseNotes";
import { toast } from "../../components/toast";
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Loading,
  PageHeader,
  Segmented,
  Switch,
  Textarea,
} from "../../components/ui";
import { fmtDate, fmtDateTime, relTime } from "../../lib/format";
import { compareSemver, fmtBytes, PLATFORM_LABEL, pushProgress } from "../../lib/releases";

export const Route = createFileRoute("/_app/releases")({
  staticData: { crumb: { label: "Releases", parents: [] } },
  loader: ({ context }) => context.queryClient.prefetchQuery(releasesQuery),
  component: ReleasesPage,
});

function ReleasesPage() {
  return (
    <>
      <PageHeader title="Releases" />
      <div className="flex flex-col gap-4">
        <ConnectedNow />
        <ReleasesTable />
        <WhatsNew />
      </div>
    </>
  );
}

// ---------- connected now ----------

function ConnectedNow() {
  const connected = useQuery(connectedQuery);
  if (!connected.data) return null;
  const { items, total, scope } = connected.data;
  const platforms = (["windows", "macos"] as const).filter((p) => items.some((i) => i.platform === p));
  return (
    <Card
      title="Connected now"
      actions={
        <span className="text-caption text-ink-3">
          {total} {total === 1 ? "app" : "apps"}
          {scope === "all" ? " across companies" : ""}
        </span>
      }
    >
      {platforms.length ? (
        <div className="flex flex-col gap-2 px-4 py-3">
          {platforms.map((p) => (
            <div key={p} className="flex flex-wrap items-center gap-2 text-ui">
              <span className="w-20 shrink-0 text-ink-2">{PLATFORM_LABEL[p]}</span>
              {items
                .filter((i) => i.platform === p)
                .map((i) => (
                  <span
                    key={i.version ?? "unknown"}
                    className="inline-flex items-center gap-1.5 rounded-pill bg-fill-2 px-2.5 py-0.5"
                  >
                    <span className="tnum font-medium">{i.version ?? "Unknown"}</span>
                    <span className="tnum text-ink-3">{i.count}</span>
                  </span>
                ))}
            </div>
          ))}
        </div>
      ) : (
        <p className="px-4 py-3 text-ui text-ink-3">No desktop apps are connected.</p>
      )}
    </Card>
  );
}

// ---------- releases ----------

const rcol = createColumnHelper<AdminRelease>();

function CopyHash({ sha256 }: { sha256: string }) {
  return (
    <Tooltip content={sha256}>
      <button
        type="button"
        aria-label={`Copy SHA-256 ${sha256}`}
        className="inline-flex items-center gap-1.5 rounded-md px-1 font-mono text-caption text-ink-2 hover:bg-fill-2 hover:text-ink"
        onClick={(e) => {
          e.stopPropagation();
          void navigator.clipboard
            ?.writeText(sha256)
            .then(() => toast("SHA-256 copied."))
            .catch(() => toast("Couldn't copy.", "danger"));
        }}
      >
        {sha256.slice(0, 12)}
        <Copy className="size-3" />
      </button>
    </Tooltip>
  );
}

function ReleasesTable() {
  const releases = useQuery(releasesQuery);
  const [openId, setOpenId] = useState<string | null>(null);

  const columns = useMemo(
    () => [
      rcol.accessor("version", {
        header: "Version",
        sortingFn: (a, b) => compareSemver(a.original.version, b.original.version),
        cell: (c) => <span className="tnum font-medium">{c.getValue()}</span>,
      }),
      rcol.accessor((r) => `${PLATFORM_LABEL[r.platform]} ${r.arch}`, {
        id: "platform",
        header: "Platform",
        cell: (c) => (
          <span className="whitespace-nowrap">
            {PLATFORM_LABEL[c.row.original.platform]}{" "}
            <span className="text-ink-3">{c.row.original.arch}</span>
          </span>
        ),
      }),
      rcol.accessor("channel", {
        header: "Channel",
        cell: (c) => (
          <Badge tone={c.getValue() === "stable" ? "neutral" : "accent"}>
            {c.getValue() === "stable" ? "Stable" : "Beta"}
          </Badge>
        ),
      }),
      rcol.accessor((r) => (r.yanked_at ? 1 : 0), {
        id: "status",
        header: "Status",
        cell: (c) =>
          c.row.original.yanked_at ? (
            c.row.original.yanked_reason ? (
              <Tooltip content={c.row.original.yanked_reason}>
                <span>
                  <Badge tone="danger">Yanked</Badge>
                </span>
              </Tooltip>
            ) : (
              <Badge tone="danger">Yanked</Badge>
            )
          ) : (
            <span className="inline-flex gap-1.5">
              <Badge tone="ok">Live</Badge>
              {c.row.original.required ? <Badge tone="warn">Required</Badge> : null}
            </span>
          ),
      }),
      rcol.accessor("published_at", {
        header: "Published",
        cell: (c) => <span className="whitespace-nowrap text-ink-2">{fmtDateTime(c.getValue())}</span>,
      }),
      rcol.accessor("size_bytes", {
        header: "Size",
        meta: { align: "right" },
        cell: (c) => <span className="tnum">{fmtBytes(c.getValue())}</span>,
      }),
      rcol.accessor("sha256", {
        header: "SHA-256",
        enableSorting: false,
        cell: (c) => <CopyHash sha256={c.getValue()} />,
      }),
      rcol.accessor("users_on_version", {
        header: "Users",
        meta: { align: "right" },
        cell: (c) => <span className="tnum">{c.getValue()}</span>,
      }),
    ],
    [],
  );

  const open = releases.data?.items.find((r) => r.id === openId) ?? null;

  return (
    <Card>
      {releases.isPending ? (
        <Loading />
      ) : releases.isError ? (
        <ErrorState error={releases.error} onRetry={() => releases.refetch()} />
      ) : (
        <DataTable
          data={releases.data.items}
          columns={columns}
          getRowId={(r) => r.id}
          initialSorting={[{ id: "published_at", desc: true }]}
          onRowClick={(r) => setOpenId(r.id)}
          rowLabel={(r) => `Release ${r.version} for ${PLATFORM_LABEL[r.platform]}`}
          rowClassName={(r) => (r.yanked_at ? "opacity-60" : undefined)}
          empty={<EmptyState title="No releases yet" />}
        />
      )}
      {open && releases.data ? (
        <ReleaseDialog release={open} canManage={releases.data.can_manage} onClose={() => setOpenId(null)} />
      ) : null}
    </Card>
  );
}

function useReleaseUpdate() {
  const qc = useQueryClient();
  return (r: AdminRelease) => {
    qc.setQueryData<ReleaseList>(qk.releases, (d) =>
      d ? { ...d, items: d.items.map((x) => (x.id === r.id ? r : x)) } : d,
    );
    void qc.invalidateQueries({ queryKey: qk.releases });
  };
}

function ReleaseDialog({
  release,
  canManage,
  onClose,
}: {
  release: AdminRelease;
  canManage: boolean;
  onClose: () => void;
}) {
  const apply = useReleaseUpdate();
  const [notes, setNotes] = useState(release.notes);
  const [channel, setChannel] = useState<ReleaseChannel>(release.channel);
  const [confirmYank, setConfirmYank] = useState(false);
  const [reason, setReason] = useState("");
  const dirty = notes !== release.notes || channel !== release.channel;

  const save = useMutation({
    mutationFn: () =>
      ops.patchRelease(release.id, {
        ...(notes !== release.notes ? { notes } : {}),
        ...(channel !== release.channel ? { channel } : {}),
      }),
    onSuccess: (r) => {
      apply(r);
      toast(`Release ${r.version} saved.`);
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  const yank = useMutation({
    mutationFn: () => ops.yankRelease(release.id, reason.trim() || null),
    onSuccess: (r) => {
      apply(r);
      setConfirmYank(false);
      toast(`Release ${r.version} yanked. The app stops offering it.`);
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  const unyank = useMutation({
    mutationFn: () => ops.unyankRelease(release.id),
    onSuccess: (r) => {
      apply(r);
      toast(`Release ${r.version} is live again. Connected apps were told.`);
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  const qc = useQueryClient();
  const push = useMutation({
    mutationFn: () => ops.pushRelease(release.id),
    onSuccess: (r) => {
      apply(r.release);
      void qc.invalidateQueries({ queryKey: qk.connected });
      toast(
        r.reached
          ? `Sent to ${r.reached} connected ${r.reached === 1 ? "app" : "apps"}.`
          : "Sent. No apps on this platform are connected right now.",
      );
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  const required = useMutation({
    mutationFn: (on: boolean) => ops.patchRelease(release.id, { required: on }),
    onSuccess: (r) => {
      apply(r);
      void qc.invalidateQueries({ queryKey: qk.connected });
      toast(
        r.required
          ? `${r.version} is required. Idle apps restart into it after a countdown.`
          : `${r.version} is optional again.`,
      );
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });

  const title = `${PLATFORM_LABEL[release.platform]} ${release.version}`;
  return (
    <>
      <Dialog
        open={!confirmYank}
        onClose={onClose}
        title={title}
        size="lg"
        footer={
          canManage ? (
            <>
              {release.yanked_at ? (
                <Button busy={unyank.isPending} onClick={() => unyank.mutate()}>
                  Unyank
                </Button>
              ) : (
                <>
                  <Button variant="danger" onClick={() => setConfirmYank(true)}>
                    Yank
                  </Button>
                  <Button busy={push.isPending} onClick={() => push.mutate()}>
                    Push to clients
                  </Button>
                </>
              )}
              <span className="flex-1" />
              <Button onClick={onClose}>Close</Button>
              <Button variant="primary" disabled={!dirty} busy={save.isPending} onClick={() => save.mutate()}>
                Save
              </Button>
            </>
          ) : (
            <Button onClick={onClose}>Close</Button>
          )
        }
      >
        <div className="flex flex-col gap-4">
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-ui">
            <dt className="text-ink-3">File</dt>
            <dd className="truncate">{release.file_name}</dd>
            <dt className="text-ink-3">Published</dt>
            <dd>{fmtDateTime(release.published_at)}</dd>
            <dt className="text-ink-3">Size</dt>
            <dd className="tnum">{fmtBytes(release.size_bytes)}</dd>
            <dt className="text-ink-3">SHA-256</dt>
            <dd className="break-all font-mono text-caption">{release.sha256}</dd>
            <dt className="text-ink-3">Users on it</dt>
            <dd className="tnum">{release.users_on_version}</dd>
            {release.pushed_at ? (
              <>
                <dt className="text-ink-3">Last pushed</dt>
                <dd>{relTime(release.pushed_at)}</dd>
              </>
            ) : null}
            {release.yanked_at ? (
              <>
                <dt className="text-ink-3">Yanked</dt>
                <dd>
                  {fmtDateTime(release.yanked_at)}
                  {release.yanked_reason ? `: ${release.yanked_reason}` : ""}
                </dd>
              </>
            ) : null}
          </dl>
          {release.yanked_at ? null : <PushProgress release={release} />}
          {canManage ? (
            <>
              {release.yanked_at ? null : (
                <Field
                  label="Required"
                  help="Idle apps restart into it after a 60 second countdown. Never during a call."
                >
                  <Switch
                    label="Required"
                    checked={release.required}
                    disabled={required.isPending}
                    onChange={(on) => required.mutate(on)}
                  />
                </Field>
              )}
              <Field label="Channel" help="Beta releases reach only apps set to beta updates.">
                <Segmented
                  label="Channel"
                  value={channel}
                  onChange={setChannel}
                  options={[
                    { value: "stable", label: "Stable" },
                    { value: "beta", label: "Beta" },
                  ]}
                />
              </Field>
              <Field label="Release notes" help="Shown in the app and on the download page.">
                <Textarea rows={6} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </Field>
            </>
          ) : (
            <section>
              <h3 className="mb-1.5 text-ui font-medium">Release notes</h3>
              <ReleaseNotes notes={release.notes} />
            </section>
          )}
        </div>
      </Dialog>

      <Dialog
        open={confirmYank}
        onClose={() => setConfirmYank(false)}
        title={`Yank ${title}?`}
        tone="danger"
        size="sm"
        footer={
          <>
            <Button onClick={() => setConfirmYank(false)}>Cancel</Button>
            <Button variant="danger" busy={yank.isPending} onClick={() => yank.mutate()}>
              Yank
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-ui">
            Apps stop offering this update and the download page falls back to the previous release. Apps that
            already installed it keep running it.
          </p>
          <Field label="Reason (optional)">
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </div>
      </Dialog>
    </>
  );
}

/** After a push: connected apps of the platform already on this version or newer. */
function PushProgress({ release }: { release: AdminRelease }) {
  const recent = release.pushed_at !== null && Date.now() - Date.parse(release.pushed_at) < 60 * 60_000;
  const connected = useQuery({ ...connectedQuery, refetchInterval: recent ? 5_000 : 10_000 });
  const data: ConnectedApps | undefined = connected.data;
  if (!data || !recent) return null;
  const { updated, total } = pushProgress(data.items, release);
  if (!total) return null;
  return (
    <p className="text-ui" aria-live="polite">
      <span className="tnum font-medium">
        {updated} of {total}
      </span>{" "}
      <span className="text-ink-2">
        connected {PLATFORM_LABEL[release.platform]} {total === 1 ? "app runs" : "apps run"} {release.version}{" "}
        or newer.
      </span>
    </p>
  );
}

// ---------- what's new ----------

function WhatsNew() {
  const q = useQuery(whatsNewQuery);
  if (!q.data) return null;
  return (
    <Card
      title="What's new"
      actions={
        <a
          href={q.data.all_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-accent-ink text-caption hover:underline"
        >
          All versions
          <ExternalLink className="size-3" aria-hidden />
        </a>
      }
    >
      {q.data.items.length ? (
        <ul className="divide-y divide-hairline">
          {q.data.items.map((w) => (
            <li key={w.version} className="flex items-start gap-4 px-4 py-2.5">
              <span className="w-24 shrink-0 font-medium tnum text-ui">{w.version}</span>
              <span className="flex min-w-0 flex-1 flex-col">
                <a
                  href={w.url}
                  target="_blank"
                  rel="noreferrer"
                  className="truncate font-medium text-ui hover:underline"
                >
                  {w.title}
                </a>
                <span className="truncate text-caption text-ink-2">{w.summary}</span>
              </span>
              <span className="hidden shrink-0 items-center gap-1.5 sm:flex">
                {w.has_video ? <Badge tone="neutral">Video</Badge> : null}
                <span className="whitespace-nowrap text-caption text-ink-3">{fmtDate(w.published_at)}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-4 py-3 text-ink-3 text-ui">Nothing published yet.</p>
      )}
    </Card>
  );
}
